import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';

/**
 * After Stripe Checkout, the user lands here with ?session_id=...
 * We poll /api/payments/status/:session_id every 2 seconds (max 5 polls)
 * until payment is paid OR we time out. On paid, /auth/me will show the
 * new tier so we just send the user to /dashboard.
 */
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 6;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get('session_id');
    const [state, setState] = useState('polling'); // polling | success | failed | timeout
    const [tier, setTier] = useState(null);
    const pollCount = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState('failed');
            return;
        }
        let cancelled = false;
        let timer;

        const poll = async () => {
            try {
                const r = await api.get(`/api/payments/status/${sessionId}`);
                if (cancelled) return;
                if (r.data.payment_status === 'paid' && r.data.subscription_applied) {
                    setTier(r.data.tier);
                    setState('success');
                    return;
                }
                if (r.data.status === 'expired') {
                    setState('failed');
                    return;
                }
                pollCount.current += 1;
                if (pollCount.current >= MAX_POLLS) {
                    setState('timeout');
                    return;
                }
                timer = setTimeout(poll, POLL_INTERVAL_MS);
            } catch (e) {
                pollCount.current += 1;
                if (pollCount.current >= MAX_POLLS) {
                    setState('timeout');
                    return;
                }
                timer = setTimeout(poll, POLL_INTERVAL_MS);
            }
        };

        poll();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [sessionId]);

    return (
        <div
            className="min-h-screen flex items-center justify-center p-6"
            style={{ background: 'linear-gradient(160deg, #EEE7FF 0%, #F5F0FF 45%, #F0E9FF 100%)' }}
            data-testid="payment-success-page"
        >
            <div className="bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(99,51,186,0.3)] p-10 max-w-md w-full text-center">
                {state === 'polling' && (
                    <>
                        <Loader2 className="w-12 h-12 text-[#7C3AED] mx-auto mb-4 animate-spin" />
                        <h1 className="text-2xl font-black text-zinc-950 mb-2">Confirming your payment…</h1>
                        <p className="text-sm text-zinc-500">This usually takes a few seconds.</p>
                    </>
                )}

                {state === 'success' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-9 h-9 text-emerald-500" />
                        </div>
                        <h1 className="text-3xl font-black text-zinc-950 mb-2">You're in! 🎉</h1>
                        <p className="text-base text-zinc-600 mb-1">
                            Your <span className="font-bold capitalize text-[#7C3AED]">{tier}</span> plan is now active.
                        </p>
                        <p className="text-sm text-zinc-500 mb-7">
                            Enjoy the full features — let's get back to learning.
                        </p>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-sm transition-colors shadow-[0_6px_20px_-6px_rgba(124,58,237,0.55)]"
                            data-testid="payment-success-continue"
                        >
                            Go to dashboard <ArrowRight className="w-4 h-4" />
                        </button>
                    </>
                )}

                {state === 'timeout' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                            <Loader2 className="w-9 h-9 text-amber-600" />
                        </div>
                        <h1 className="text-2xl font-black text-zinc-950 mb-2">Hang tight</h1>
                        <p className="text-sm text-zinc-600 mb-6">
                            Your payment is processing. Please refresh in a minute, or visit your dashboard.
                        </p>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="w-full py-3.5 rounded-xl bg-zinc-950 text-white font-bold text-sm"
                        >
                            Continue to dashboard
                        </button>
                    </>
                )}

                {state === 'failed' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                            <XCircle className="w-9 h-9 text-rose-500" />
                        </div>
                        <h1 className="text-2xl font-black text-zinc-950 mb-2">Payment didn't complete</h1>
                        <p className="text-sm text-zinc-600 mb-6">
                            You can try again whenever you're ready.
                        </p>
                        <Link
                            to="/"
                            className="block w-full py-3.5 rounded-xl bg-zinc-950 text-white font-bold text-sm"
                        >
                            Back to home
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
